import type { QuestionProps } from '../eform.types'

const PHONE_KEYWORDS = /phone|cell|mobile|fax|telephone/i
const EMAIL_KEYWORDS = /e-?mail/i

function formatPhone(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

export function detectFieldType(questionContent: string): 'phone' | 'email' | 'text' {
  const plain = questionContent.replace(/<[^>]*>/g, '')
  if (PHONE_KEYWORDS.test(plain)) return 'phone'
  if (EMAIL_KEYWORDS.test(plain)) return 'email'
  return 'text'
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export default function TextQuestion({ question, value, onChange, error }: QuestionProps) {
  const strValue = Array.isArray(value) ? value[0] ?? '' : value
  const fieldType = detectFieldType(question.questionContent)

  function handlePhoneChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 10)
    onChange(formatPhone(digits))
  }

  if (fieldType === 'phone') {
    const phoneDigits = strValue.replace(/\D/g, '')
    const phoneValid = phoneDigits.length === 10
    const phoneStarted = phoneDigits.length > 0

    return (
      <div>
        <input
          type="tel"
          inputMode="numeric"
          value={strValue}
          onChange={(e) => handlePhoneChange(e.target.value)}
          placeholder="(555) 123-4567"
          maxLength={14}
          className={`w-full rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
            error ? 'border-red-300' : phoneValid ? 'border-green-400' : 'border-slate-200'
          }`}
        />
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
        {!error && phoneStarted && (
          phoneValid
            ? <p className="text-xs text-green-600 mt-1">Valid phone number</p>
            : <p className="text-xs text-slate-400 mt-1">{10 - phoneDigits.length} digit{10 - phoneDigits.length !== 1 ? 's' : ''} remaining</p>
        )}
      </div>
    )
  }

  if (fieldType === 'email') {
    const emailValid = isValidEmail(strValue)
    const emailStarted = strValue.trim().length > 0

    return (
      <div>
        <input
          type="email"
          inputMode="email"
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="name@example.com"
          className={`w-full rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
            error ? 'border-red-300' : emailValid ? 'border-green-400' : 'border-slate-200'
          }`}
        />
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
        {!error && emailStarted && (
          emailValid
            ? <p className="text-xs text-green-600 mt-1">Valid email address</p>
            : <p className="text-xs text-slate-400 mt-1">Enter a valid email address</p>
        )}
      </div>
    )
  }

  return (
    <div>
      <input
        type="text"
        value={strValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Your answer"
        className={`w-full rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
          error ? 'border-red-300' : 'border-slate-200'
        }`}
      />
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}
